import { FC, useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import { Delete, DescriptionOutlined, SettingsOutlined, CheckCircleOutline } from "@mui/icons-material";
import { toLower, upperFirst } from "lodash";
import { a } from "@react-spring/web";
import { v4 as uuidv4 } from "uuid";

import BillingSessionNav from "../../../common/pos/billing-session-nav/billing-session-nav";
import CustomerDetailSection from "../../../common/pos/customer-detail-section/customer-detail-section";
import CartSection from "../../../common/pos/cart-section/cart-section";
import ManagerCodeModal from "../../../common/manager-code-modal/ManagerCodeModal";
import CustomMarquee from "../../../common/pos/marquee/marquee";
import OrderDetailSection from "../../../common/pos/order-detail-section/order-detail-section";
import CartQtyHandlerSection from "../../../common/pos/cart-qty-handler-section/cart-qty-handler-section";
import { Colors } from "../../../../configs";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import {
  addSession,
  newBillAction,
  removeCartItem,
} from "../../../../redux/actions/cartAction";
import useThermalPrinter from "../../../../hooks/thermalPrinter";

import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PosSliceAction } from "../../../../redux/slice/posSlice";
import { Service } from "../../../../typings/enum/Service";
import PaymentModal from "../../payment-modal/payment-modal";
import useCart from "../../../../hooks/useCart";
import { getPricing } from "../../../../redux/actions/pricingAction";
import { PaymentModalContents } from "../../../../typings/enum/paymentModalContents";
import { monthNames } from "../../../../data";
import BillNotesModal from "../../notes-modal/billNotesModal";
import { getUserNotesByIdAction } from "../../../../redux/actions/customerAction";
import { useUserNote } from "../../../../providers/UserNoteProvider";
import { NoteOccasion } from "../../../../typings/enum/noteOccasions";
import { CustomButton, PosButton } from "../../../atoms";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import { AssignPinTagModal } from "../../../common/pos/assign-pin-tag-modal/assign-pin-tag-modal";
import { CartSliceAction } from "../../../../redux/slice/cartSlice";
import { BillPickupStatus } from "../../../../typings/enum/paymentTypes";
import { DiscountSliceAction } from "../../../../redux/slice/discountSlice";
import { ICustomerBill } from "../../../../typings";
import { ICartSession } from "../../../../typings/interfaces/pos/cart-session";
import ConfirmDialog from "../../../atoms/confirm-box/confirm-box";
import WarningDialog from "../../../atoms/warning-dialog/WarningDialog";

export interface ICartProps {
  editingBillId?: string;
}

const Cart: FC<ICartProps> = ({ editingBillId }) => {
  const [openCheckInMdal, setOpenCheckInMdal] = useState(false);
  const [openNotesModal, setOpenNotesModal] = useState(false);
  const [openAssignPinTagModal, setOpenAssignPinTagModal] = useState(false);
  const [openClearCartDialog, setOpenClearCartDialog] = useState(false);

  const [openMixedServiceWarning, setOpenMixedServiceWarning] = useState(false);
  const [mixedServiceWarningMessage, setMixedServiceWarningMessage] = useState("");
  const [openManagerCodeModal, setOpenManagerCodeModal] = useState(false);

  const [openActionTileModal, setOpenActionTileModal] =
    useState<boolean>(false);
  const [isCheckInSuccessful, setIsCheckInSuccessful] = useState<boolean>(true);

  // Long press functionality
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const longPressDelay = 1000; // 1 second

  const {
    cart,
    selectedCartItem,
    selectedSession,
    selectedServiceType,
    cartMode,
    billByIdLoading,
    billById,
    billItemIdList,
  } = useAppSelector((state) => state.cart);
  const { cartTotal, totalDiscountAmount } = useCart();
  const { customer, customerNotes, customerLoading } = useAppSelector(
    (state) => state.customer
  );

  const { activeServiceTab } = useAppSelector((state) => state.pos);
  const { user } = useAppSelector((state) => state.auth);
  const { configure, isConfigured } = useThermalPrinter();
  const { showNote } = useUserNote();
  const snackbar = useSnackbarContext();

  const navigate = useNavigate();
  // const [marqueeText, setMarqueeText] = useState("");
  const dispatch = useAppDispatch();
  const params = useParams();
  const location = useLocation();
  const billId = params.billId;

  const customerId = params.id;

  // Defense-in-depth mirroring CartRow's own selection guard - an
  // already-picked-up or already-paid-for item (Edit Invoice, against a
  // saved bill) should never actually reach the Delete action even if
  // `selectedCartItem` somehow ends up pointing at one.
  const isSelectedItemLocked = useMemo(() => {
    if (!selectedCartItem) return false;
    const entry = billItemIdList.find((b) => b.billId === billById?.id);
    return (
      entry?.billItemIdList.some(
        (item) =>
          item.hashId === selectedCartItem.itemId &&
          (item.pickupStatus === BillPickupStatus.PICKED_UP || item.isPaid === true)
      ) ?? false
    );
  }, [billItemIdList, billById?.id, selectedCartItem]);

  const handleOnClickDelete = () => {
    if (isSelectedItemLocked) return;
    dispatch(removeCartItem());
  };

  // Long press handlers for clear all cart items
  const handleMouseDown = () => {
    longPressTimer.current = setTimeout(() => {
      setOpenClearCartDialog(true);
    }, longPressDelay);
  };

  const handleMouseUp = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleMouseLeave = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // Clear all cart items
  const handleClearAllCartItems = () => {
    dispatch(CartSliceAction.clearSession());
    dispatch(DiscountSliceAction.resetDiscount());

    // Create a new empty session so users can continue adding items
    const newSession: ICartSession = {
      sessionId: uuidv4(),
      cartItems: [],
      customerId: customerId ? parseInt(customerId) : 0,
      pinTag: {
        color: null,
        tags: [],
        date: null,
      },
    };

    dispatch(addSession(newSession));
    dispatch(CartSliceAction.setSelectedSession(newSession));

    setOpenClearCartDialog(false);
    snackbar.showSnackbar("All cart items have been cleared", "success");
  };

  // Cancel clear cart dialog
  const handleCancelClearCart = () => {
    setOpenClearCartDialog(false);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchNotes = async () => {
      if (customerId) {
        const res: any = await dispatch(
          getUserNotesByIdAction(customerId as string)
        );
        showNote(NoteOccasion.ON_CHECKIN, res.payload);
      }
    };

    fetchNotes();
  }, [customerId, dispatch]);

  const loadNotes = async () => {
    if (customerId) {
      await dispatch(getUserNotesByIdAction(customerId as string)).then(
        (res: any) => {
          showNote(NoteOccasion.ON_CHECKIN, res.payload);
        }
      );
    }
  };

  const changeCartMode = (mode: "check-in" | "pickup") => {
    if (cartMode !== mode) {
      dispatch(CartSliceAction.setCartMode(mode));
    }
    dispatch(CartSliceAction.clearSession());
    dispatch(DiscountSliceAction.resetDiscount());
  };

  useEffect(() => {
    changeCartMode("check-in");
  }, [cartMode, location.pathname]);

  useEffect(() => {
    if (selectedServiceType?.id)
      dispatch(getPricing({ id: selectedServiceType?.id }));
  }, [selectedServiceType?.id]);

  const handleOnClickCustomization = () => {
    if (activeServiceTab === Service.CUSTOMIZATION) {
      dispatch(CartSliceAction.setSelectedCartItem(null));
    }
    dispatch(
      PosSliceAction.setActiveServiceTab(
        activeServiceTab === Service.GARMENT_WASH_AND_CLEAN
          ? Service.CUSTOMIZATION
          : Service.GARMENT_WASH_AND_CLEAN
      )
    );
  };

  const handleOnCloseCheckInModal = () => {
    setOpenCheckInMdal(false);
  };
  const handleOnCloseNotesModal = () => {
    setOpenNotesModal(false);
  };

  const handleOnCheckIn = () => {
    let isEmptySession = true;

    for (const session of cart) {
      const finalizedItems = session.cartItems?.filter((item) => item.finalized) || [];

      if (finalizedItems.length > 0) {
        isEmptySession = false;

        let hasPressOnly = false;
        let hasCleanOnly = false;
        let hasRedo = false;
        let hasStandard = false;

        finalizedItems.forEach(item => {
          if (item.redoDetails) {
            hasRedo = true;
          } else if (item.pressOnly) {
            hasPressOnly = true;
          } else if (item.cleanOnly) {
            hasCleanOnly = true;
          } else {
            hasStandard = true;
          }
        });

        // Validation Logic
        if (hasPressOnly && (hasCleanOnly || hasRedo || hasStandard)) {
          setMixedServiceWarningMessage("Press-only garments cannot be listed with any other service options.\n\nPlease remove all non press-only items to proceed!");
          setOpenMixedServiceWarning(true);
          return;
        }

        if (hasCleanOnly && (hasPressOnly || hasRedo || hasStandard)) {
          setMixedServiceWarningMessage("Clean-only garments cannot be listed with any other service options.\n\nPlease remove all non clean-only items to proceed!");
          setOpenMixedServiceWarning(true);
          return;
        }

        if (hasRedo && (hasPressOnly || hasCleanOnly || hasStandard)) {
          setMixedServiceWarningMessage("Redo garments cannot be listed with any other service options.\n\nPlease remove all non redo items to proceed!");
          setOpenMixedServiceWarning(true);
          return;
        }
      }
    }


    if (isEmptySession) {
      snackbar.showSnackbar(
        "Please add items to the empty session before check in",
        "warning"
      );
    } else {
      let isRedoBill = false;
      cart.forEach((session) => {
        if (session.cartItems?.some((item) => item.redoDetails)) {
          isRedoBill = true;
        }
      });

      if (isRedoBill) {
        setOpenManagerCodeModal(true);
      } else {
        proceedToCheckIn();
      }
    }
  };

  const proceedToCheckIn = () => {
    setOpenManagerCodeModal(false);
    const lastSessionPinTag = cart[cart.length - 1]?.pinTag;
    // A carried-forward pin tag (see cartAction.ts's resizePinTagForSession)
    // can be non-empty but still incomplete - e.g. item count went up and a
    // new slot was appended blank. Checking tags.length === 0 alone would
    // treat that as "complete" and skip straight to checkout with a missing
    // tag, so require every slot to actually have a value.
    const hasIncompleteTags =
      !lastSessionPinTag?.tags?.length ||
      lastSessionPinTag.tags.some((tag) => !tag);
    if (lastSessionPinTag?.color == null || hasIncompleteTags) {
      setOpenAssignPinTagModal(true);
    } else {
      setOpenCheckInMdal(true);
    }
  };
  let isEmptySession = true;
  cart.forEach((session) => {
    if (session.cartItems?.filter((item) => item.finalized).length > 0) {
      isEmptySession = false;
    } else {
      isEmptySession = true;
      return;
    }
  });

  const handleOpenModal = () => {
    setOpenActionTileModal(true);
  };

  return (
    <Box
      sx={{
        flex: 4,
        display: "flex",
        flexDirection: "column",
        gap: "0.57rem",
        height: "100%",
        maxHeight: "100% !important",
        width: "100%",
        maxWidth: "100%",
        justifyContent: "space-between",
      }}
    >
      <AssignPinTagModal
        isOpen={openAssignPinTagModal}
        onClose={() => setOpenAssignPinTagModal(false)}
        serviceTypeId={
          cart[cart.length - 1]?.cartItems[0]?.serviceType?.id ?? 0
        }
        itemsCount={
          cart[cart.length - 1]?.cartItems.reduce(
            (acc, item) => (item.finalized ? acc + item.qty : acc),
            0
          ) ?? 0
        }
        sessionId={cart[cart.length - 1]?.sessionId ?? ""}
        initialColor={cart[cart.length - 1]?.pinTag?.color ?? undefined}
        initialDate={cart[cart.length - 1]?.pinTag?.date ?? undefined}
        initialTags={cart[cart.length - 1]?.pinTag?.tags ?? undefined}
        pinTagDraftSession={{
          sessionId: cart[cart.length - 1]?.sessionId ?? "",
          customerId: cart[cart.length - 1]?.customerId ?? 0,
          cartItems: cart[cart.length - 1]?.cartItems ?? [],
          pinTag: { color: null, tags: [], date: null },
        }}
        onSubmit={async (updatedSession) => {
          await dispatch(addSession(updatedSession));
          dispatch(CartSliceAction.setSelectedSession(updatedSession));
          dispatch(
            CartSliceAction.setSelectedServiceType(
              updatedSession.cartItems[0]?.serviceType || null
            )
          );
          setOpenCheckInMdal(true);
          setOpenAssignPinTagModal(false);
        }}
      />

      <PaymentModal
        total={cartTotal}
        discount={totalDiscountAmount}
        open={openCheckInMdal}
        onClose={handleOnCloseCheckInModal}
        // onCheckout={handleOnCheckout}
        content={PaymentModalContents.SUMMARY}
      />

      {/* Clear Cart Confirmation Dialog */}
      <ConfirmDialog
        open={openClearCartDialog}
        title="Clear Cart"
        message="Do you really want to clear all cart items?"
        positiveButtonText="Yes"
        negativeButtonText="No"
        onConfirm={handleClearAllCartItems}
        onCancel={handleCancelClearCart}
      />

      {/* Mixed Service Warning Dialog */}
      <WarningDialog
        open={openMixedServiceWarning}
        onClose={() => setOpenMixedServiceWarning(false)}
        onOk={() => setOpenMixedServiceWarning(false)}
        message={mixedServiceWarningMessage}
      />

      <ManagerCodeModal
        open={openManagerCodeModal}
        onClose={() => setOpenManagerCodeModal(false)}
        title="Manager Code"
        task="access the Redo Check In"
        onDone={proceedToCheckIn}
        actionType="REDO_CHECK_IN"
        requestedByUserId={user?.id}
        billId={billById?.id}
      />

      <BillingSessionNav editingBillId={editingBillId} />
      <CustomerDetailSection
        showAnalysisButton={!!!editingBillId}
        showEditButton={!!!editingBillId}
        showHistoryButton={!!!editingBillId}
      />

      <CartSection onSelect={() => { }} />

      <CustomMarquee
        text={(() => {
          const notesText =
            customerNotes == null || customerNotes.length === 0
              ? ""
              : customerNotes
                .filter((note) =>
                  note.noteShowOccasion?.includes(NoteOccasion.ON_CHECKIN)
                )
                .map((note) => note.note)
                .join(", ");

          const alterationText =
            customer?.automaticAlteration
              ? `Automatic Alteration: ON | Allowed Amount: $${customer.allowedAmount ?? "0"}`
              : "";

          return [notesText, alterationText].filter(Boolean).join("   |   ");
        })()}
      />
      {/* <Marquee>{marqueeText}</Marquee> */}

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "1.07142857rem",
          flexShrink: 0,
        }}
      >
        <OrderDetailSection />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.71rem",
            maxHeight: "16.07142857142857rem",
            flex: 1,
          }}
        >
          <CartQtyHandlerSection />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: "0.71rem",
              flexWrap: "nowrap",
              width: "100%",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <IconButton
              sx={{
                background: Colors.MAIN_GRAY,
                height: "3.714285714285714rem",
                width: "3.571428571428571rem",
                aspectRatio: "1/1",
                flexShrink: 0,
                borderRadius: "1rem",
                "&:hover": { background: `${Colors.MAIN_GRAY} !important` },
                "&:disabled": { background: `${Colors.MAIN_GRAY} !important` },
              }}
              disabled={selectedCartItem ? isSelectedItemLocked : true}
              onClick={handleOnClickDelete}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              name="delete-cart-item"
            >
              <Delete
                sx={{
                  color: Colors.WHITE,
                  fontSize: "1.78571429rem",
                }}
              />
            </IconButton>
            <BillNotesModal
              open={openNotesModal}
              onClose={handleOnCloseNotesModal}
            />
            <PosButton
              test-name="notes-button"
              variantType="secondary"
              sx={{
                flex: 1,
                width: {
                  xs: "7.314285714285714rem",
                  sm: "9.314285714285714rem",
                },
                minWidth: {
                  xs: "7.314285714285714rem",
                  sm: "9.314285714285714rem",
                },
                gap: "0.4rem",
              }}
              onClick={() => setOpenNotesModal(true)}
              disabled={isEmptySession}
            >
              <DescriptionOutlined sx={{ fontSize: "1.6rem" }} />
              Notes
            </PosButton>
            <PosButton
              variantType="secondary"
              disabled={
                selectedCartItem?.finalized === true
                  ? isSelectedItemLocked
                  : true
              }
              sx={{
                flex: 1,
                width: "13.78571428571429rem",
                minWidth: "13.78571428571429rem",
                gap: "0.4rem",
              }}
              onClick={handleOnClickCustomization}
            >
              <SettingsOutlined sx={{ fontSize: "1.6rem" }} />
              {activeServiceTab === Service.GARMENT_WASH_AND_CLEAN
                ? "Customization"
                : "Add Items"}
            </PosButton>
          </Box>

          <PosButton
            onClick={handleOnCheckIn}
            name="check-in"
            loading={billByIdLoading || customerLoading}
            sx={{ gap: "0.6rem" }}
          >
            <CheckCircleOutline sx={{ fontSize: "2.28rem" }} />
            {billId ? "Update Bill" : "Check In"}
          </PosButton>

          {/* <CustomButton
            onClick={configure}
            disabled={isConfigured}
            sx={{ background: Colors.ACCENT_GRAY, height: "" }} //a minimum height is temporary assigned for the button
          >
            Config Printer
          </CustomButton> */}
        </Box>
      </Box>
    </Box>
  );
};

export default Cart;
